import { useRef, useEffect, useCallback } from 'react';
import './Editor.css';

// Jertscript keywords organized by category for syntax highlighting
const KEYWORDS = {
  // Variable declarations (blue)
  declarations: ['croissant', 'baguette'],
  // Control flow (purple)
  controlFlow: ['franchement', 'sinon', 'pendant', 'pour', 'fonction', 'retourner', 'malheureusement', 'merde', 'quandmeme'],
  // Async/class keywords (purple)
  async: ['asynchrone', 'attendre', 'classe', 'nouveau', 'ceci'],
  // Modules (purple)
  modules: ['importer', 'exporter', 'de'],
  // Literals (orange)
  literals: ['oui', 'non', 'rien'],
  // Operators (cyan)
  operators: ['et', 'ou', 'pas', 'est', 'nest_pas'],
};

// Built-in functions
const BUILTINS = ['dire', 'crier', 'demander'];

// Flatten all keywords for autocomplete
const ALL_KEYWORDS = [
  ...KEYWORDS.declarations,
  ...KEYWORDS.controlFlow,
  ...KEYWORDS.async,
  ...KEYWORDS.modules,
  ...KEYWORDS.literals,
  ...KEYWORDS.operators,
  ...BUILTINS,
];

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Escapes HTML special characters to prevent XSS and render correctly
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Applies syntax highlighting to Jertscript code
 * Returns HTML string with span elements for colored tokens
 */
function highlightCode(code: string): string {
  // First escape HTML
  let result = escapeHtml(code);

  // Highlight strings (must be done before keywords to avoid highlighting inside strings)
  // Handle both single and double quoted strings
  result = result.replace(
    /(&quot;(?:[^&]|&(?!quot;))*?&quot;|'(?:[^'\\]|\\.)*?')/g,
    '<span class="jert-string">$1</span>'
  );

  // Handle actual double quotes in the source
  result = result.replace(
    /(\"(?:[^\"\\]|\\.)*?\")/g,
    '<span class="jert-string">$1</span>'
  );

  // Highlight comments (// and /* */)
  result = result.replace(
    /(\/\/[^\n]*)/g,
    '<span class="jert-comment">$1</span>'
  );

  // Highlight multi-line comments
  result = result.replace(
    /(\/\*[\s\S]*?\*\/)/g,
    '<span class="jert-comment">$1</span>'
  );

  // Highlight numbers
  result = result.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span class="jert-number">$1</span>'
  );

  // Highlight declaration keywords (croissant, baguette)
  const declarationPattern = new RegExp(`\\b(${KEYWORDS.declarations.join('|')})\\b`, 'g');
  result = result.replace(declarationPattern, '<span class="jert-declaration">$1</span>');

  // Highlight control flow keywords
  const controlFlowPattern = new RegExp(`\\b(${KEYWORDS.controlFlow.join('|')})\\b`, 'g');
  result = result.replace(controlFlowPattern, '<span class="jert-keyword">$1</span>');

  // Highlight async/class keywords
  const asyncPattern = new RegExp(`\\b(${KEYWORDS.async.join('|')})\\b`, 'g');
  result = result.replace(asyncPattern, '<span class="jert-keyword">$1</span>');

  // Highlight module keywords
  const modulePattern = new RegExp(`\\b(${KEYWORDS.modules.join('|')})\\b`, 'g');
  result = result.replace(modulePattern, '<span class="jert-keyword">$1</span>');

  // Highlight literals (oui, non, rien)
  const literalPattern = new RegExp(`\\b(${KEYWORDS.literals.join('|')})\\b`, 'g');
  result = result.replace(literalPattern, '<span class="jert-literal">$1</span>');

  // Highlight operators
  const operatorPattern = new RegExp(`\\b(${KEYWORDS.operators.join('|')})\\b`, 'g');
  result = result.replace(operatorPattern, '<span class="jert-operator">$1</span>');

  // Highlight built-in functions
  const builtinPattern = new RegExp(`\\b(${BUILTINS.join('|')})(?=\\s*\\()`, 'g');
  result = result.replace(builtinPattern, '<span class="jert-builtin">$1</span>');

  // Highlight function calls (identifier followed by parenthesis)
  result = result.replace(
    /\b([a-zA-Z_][a-zA-Z0-9_]*)(?=\s*\()/g,
    (match, name) => {
      // Don't re-highlight if already highlighted
      if (BUILTINS.includes(name) ||
          KEYWORDS.controlFlow.includes(name) ||
          KEYWORDS.async.includes(name)) {
        return match;
      }
      return `<span class="jert-function">${name}</span>`;
    }
  );

  return result;
}

export function Editor({ value, onChange, placeholder }: EditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  // Sync scroll between textarea and highlight layer
  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current && lineNumbersRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Calculate line numbers
  const lineNumbers = value.split('\n').map((_, i) => i + 1);

  // Handle tab key for indentation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Insert 2 spaces for tab
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      onChange(newValue);

      // Move cursor after the inserted spaces
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    }
  }, [value, onChange]);

  // Update line numbers height to match content
  useEffect(() => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className="jert-editor">
      <div className="jert-editor-line-numbers" ref={lineNumbersRef}>
        {lineNumbers.map(num => (
          <div key={num} className="jert-line-number">{num}</div>
        ))}
      </div>
      <div className="jert-editor-content">
        <pre
          ref={highlightRef}
          className="jert-editor-highlight"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: highlightCode(value) + '\n' }}
        />
        <textarea
          ref={textareaRef}
          className="jert-editor-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
        />
      </div>
    </div>
  );
}

// Export keywords for autocomplete feature
export { ALL_KEYWORDS, KEYWORDS, BUILTINS };
